import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

export default function ReservedSpotsManager() {
  const [reservedSpots, setReservedSpots] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [totalCapacity, setTotalCapacity] = useState(250);
  const [currentRegistrations, setCurrentRegistrations] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Get reserved spots from system settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'reserved_spots')
        .maybeSingle(); // Changed from single() to maybeSingle() to handle no rows
        
      if (!settingsError && settingsData) {
        const reservedValue = typeof settingsData.value === 'number' 
          ? settingsData.value 
          : (typeof settingsData.value === 'string' 
            ? parseInt(settingsData.value) 
            : parseInt(settingsData.value.toString()));
            
        setReservedSpots(isNaN(reservedValue) ? 0 : reservedValue);
      }
      
      // Get tournament capacity
      const { data: tournamentData } = await supabase
        .from('tournaments')
        .select('id, capacity')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true })
        .limit(1)
        .maybeSingle();

      const capacity = tournamentData?.capacity ?? 250;
      const tournamentId = tournamentData?.id ?? null;
      setTotalCapacity(capacity);

      // Get current player count scoped to the upcoming tournament
      let countQuery = supabase
        .from('players')
        .select('*', { count: 'exact', head: true });

      if (tournamentId) {
        countQuery = countQuery.eq('tournament_id', tournamentId);
      }

      const { count, error: countError } = await countQuery;

      if (!countError && count !== null) {
        setCurrentRegistrations(count);
      }
    } catch (error) {
      console.error('Error fetching reserved spots:', error);
      toast.error('Failed to load reserved spots data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Check if the setting already exists
      const { data: existingData, error: checkError } = await supabase
        .from('system_settings')
        .select('id')
        .eq('key', 'reserved_spots')
        .maybeSingle(); // Changed from single() to maybeSingle() to handle no rows
        
      if (checkError && !checkError.message.includes('No rows found')) {
        throw checkError;
      }
      
      if (existingData) {
        // Update existing setting
        const { error: updateError } = await supabase
          .from('system_settings')
          .update({ 
            value: reservedSpots,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingData.id);
          
        if (updateError) throw updateError;
      } else {
        // Create new setting
        const { error: insertError } = await supabase
          .from('system_settings')
          .insert([{
            key: 'reserved_spots',
            value: reservedSpots,
            category: 'registration',
            description: 'Number of spots reserved for special registrations',
            is_public: true
          }]);
          
        if (insertError) throw insertError;
      }
      
      toast.success('Reserved spots updated successfully');
    } catch (error) {
      console.error('Error saving reserved spots:', error);
      toast.error('Failed to update reserved spots');
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate available spots
  const availableSpots = totalCapacity - reservedSpots - currentRegistrations;
  const reservedPercentage = (reservedSpots / totalCapacity) * 100;
  const registeredPercentage = (currentRegistrations / totalCapacity) * 100;
  const availablePercentage = 100 - reservedPercentage - registeredPercentage;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <h2 className="text-xl font-semibold text-golf-gray mb-6">Registration Capacity Management</h2>
      
      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Total Capacity</div>
              <div className="text-2xl font-bold text-golf-gray">{totalCapacity}</div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Current Registrations</div>
              <div className="text-2xl font-bold text-golf-gray">{currentRegistrations}</div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Available Spots</div>
              <div className="text-2xl font-bold text-golf-gray">{availableSpots}</div>
            </div>
          </div>
          
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="reservedSpots" className="block text-sm font-medium text-gray-700">
                Reserved Spots
              </label>
              <span className="text-sm text-gray-500">{reservedSpots} spots</span>
            </div>
            <input
              type="range"
              id="reservedSpots"
              min="0"
              max={totalCapacity - currentRegistrations}
              value={reservedSpots}
              onChange={(e) => setReservedSpots(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0</span>
              <span>{Math.floor((totalCapacity - currentRegistrations) / 2)}</span>
              <span>{totalCapacity - currentRegistrations}</span>
            </div>
            
            <div className="mt-4">
              <label htmlFor="reservedSpotsInput" className="block text-sm font-medium text-gray-700 mb-1">
                Enter exact number:
              </label>
              <div className="flex gap-4">
                <input
                  type="number"
                  id="reservedSpotsInput"
                  min="0"
                  max={totalCapacity - currentRegistrations}
                  value={reservedSpots}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 0 && value <= (totalCapacity - currentRegistrations)) {
                      setReservedSpots(value);
                    }
                  }}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                />
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 bg-golf-orange text-white font-medium rounded-lg hover:bg-golf-orange/90 transition-colors disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Registration Capacity Breakdown</h3>
            <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden">
              <div className="flex h-full">
                <div 
                  className="bg-green-500 h-full"
                  style={{ width: `${registeredPercentage}%` }}
                />
                <div 
                  className="bg-gray-500 h-full"
                  style={{ width: `${reservedPercentage}%` }}
                />
                <div 
                  className="bg-blue-500 h-full"
                  style={{ width: `${availablePercentage}%` }}
                />
              </div>
            </div>
            <div className="flex justify-between mt-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-xs text-gray-600">Registered ({currentRegistrations})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                <span className="text-xs text-gray-600">Reserved ({reservedSpots})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-xs text-gray-600">Available ({availableSpots})</span>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-800 mb-1">About Reserved Spots</h4>
              <p className="text-sm text-blue-700">
                Reserved spots are held for special registrations such as VIP players, sponsors, or tournament officials. 
                These spots are not available for general registration. The system will automatically prevent regular 
                registrations once the available capacity (total capacity minus reserved spots) is reached.
              </p>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
}